import { useMemo, useCallback } from "react";
import { Spin } from "antd";
import type { MenuProps } from "antd";
import classNames from "classnames";
import { TagFilled, CloseCircleFilled, TagOutlined } from "@ant-design/icons";
import { getTagColorHex } from "@common";
import styles from "../../styles.module.less";

interface UseTagSubmenuParams {
  availableTags: Array<{ id: string; tagName: string; description: string }>;
  loadingTags: boolean;
  tags: Array<{ description: string; id: number; tagName: string }>;
  onTagToggle: (tagId: string) => void;
}

export const useTagSubmenu = ({
  availableTags,
  loadingTags,
  tags,
  onTagToggle,
}: UseTagSubmenuParams): MenuProps["items"] => {
  const selectedTagIds = useMemo(() => {
    return tags.map((tag) => String(tag.id));
  }, [tags]);

  const isTagSelected = useCallback(
    (tagId: string | number) => {
      return selectedTagIds.includes(String(tagId));
    },
    [selectedTagIds]
  );

  return useMemo(() => {
    if (loadingTags) {
      return [
        {
          key: "loading",
          label: (
            <div className={styles.menuLoading}>
              <Spin size="small" />
            </div>
          ),
          disabled: true,
        },
      ];
    }

    if (availableTags.length === 0) {
      return [
        {
          key: "empty",
          label: <div className={styles.menuEmpty}>Không có thẻ nào</div>,
          disabled: true,
        },
      ];
    }

    return availableTags.map((tag) => {
      const tagColor = getTagColorHex(tag.id);
      const isSelected = isTagSelected(tag.id);

      return {
        key: `tag-${tag.id}`,
        label: (
          <div
            className={classNames(styles.tagSubmenuItem, {
              [styles.tagSubmenuItemSelected]: isSelected,
            })}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (!isSelected) {
                onTagToggle(String(tag.id));
              }
            }}
          >
            {isSelected ? (
              <TagFilled style={{ color: tagColor }} />
            ) : (
              <TagOutlined style={{ color: tagColor }} />
            )}
            <span className={styles.tagSubmenuLabel}>{tag.tagName}</span>
            {isSelected && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onTagToggle(String(tag.id));
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px",
                  cursor: "pointer",
                  marginLeft: "auto",
                }}
              >
                <CloseCircleFilled
                  style={{ color: "#ff4d4f", fontSize: "16px" }}
                />
              </div>
            )}
          </div>
        ),
        className: isSelected ? styles.tagMenuItemSelected : undefined,
        onClick: ({
          domEvent,
        }: {
          domEvent?: React.MouseEvent | React.KeyboardEvent;
        }) => {
          domEvent?.stopPropagation();
          if (domEvent && "preventDefault" in domEvent) {
            domEvent.preventDefault();
          }
          return false;
        },
      };
    });
  }, [availableTags, loadingTags, isTagSelected, onTagToggle]);
};
